import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

import deleteIcon from '../../../../../../Assets/images/delete-icon.svg';
import filterIcon from '../../../../../../Assets/images/filter-icon.svg';
import clearIcon from '../../../../../../Assets/images/clear-icon.svg';
import editIcon from "../../../../../../Assets/images/edit-icon.svg";
import plusIcon from '../../../../../../Assets/images/plus-icon.svg';
import closeRedIcon from "../../../../../../Assets/images/close-red.svg";
import calendarIcon from "../../../../../../Assets/images/calender.svg";
import searchIcon from "../../../../../../Assets/images/search-icon.svg";

import AddDriverModal from '../AddDriversModal/AddDriversModal';
import EditDriverModal from '../EditDriversModal/EditDriversModal';
import ClipLoader from "react-spinners/ClipLoader";
import ConfirmationModal from '../../../../../../Components/ConfirmationBox/ConfirmationModal';
import apiClient from '../../../../../../Config/apiClient';
import { showErrorToast, showSuccessToast } from '../../../../../../Context/Toaster/Toaster';
import { DATE_FORMAT } from '../../../Utils/Constants';

const driversPath = process.env.REACT_APP_TC_DRIVERS_BASE_PATH;
const rolesPath = process.env.REACT_APP_TC_ROLES_BASE_PATH;

const DriversList = ({ canEditDriver }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [driversPerPage, setDriversPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [editDriver, setEditDriver] = useState(null);
    const [selectedDriverIds, setSelectedDriverIds] = useState([]);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [filterRoles, setFilterRoles] = useState([]);
    const [filterRoleId, setFilterRoleId] = useState('');
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [filtersApplied, setFiltersApplied] = useState(false);

    const filterRef = useRef(null);

    const onSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const fetchDriverDetails = async (driverId) => {
        try {
            const res = await apiClient.get(`${driversPath}/details/${driverId}`);
            if (res.data.status === 'SUCCESS') {
                return res.data.data;
            } else {
                showErrorToast(res.data.message || 'Failed to fetch driver details');
                return null;
            }
        } catch {
            showErrorToast('Error fetching driver details');
            return null;
        }
    };

    const fetchDrivers = async (payload = {}) => {
        try {
            setLoading(true);
            const res = await apiClient.post(`${driversPath}/list`, payload);
            if (res.data.status === 'SUCCESS') {
                setDrivers(res.data.data);

            } else {
                showErrorToast(res.data.message || 'Failed to fetch drivers');
            }
        } catch {
            showErrorToast('Error fetching drivers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (!params.get('autoEditSelf') || loading || drivers.length === 0) return;
        // Clear the param immediately so refresh doesn't re-trigger
        navigate('/tc/drivers', { replace: true });
        try {
            const token = localStorage.getItem('accessToken');
            if (!token) return;
            const decoded = jwtDecode(token);
            const myDriver = drivers.find(d => d.id === decoded.id);
            if (!myDriver) return;
            fetchDriverDetails(myDriver.id).then(driverData => {
                if (driverData) setEditDriver({ ...driverData, user_role: myDriver.user_role, editMode: true });
            });
        } catch { /* invalid token */ }
    }, [loading, location.search]);

    useEffect(() => {
        apiClient.get(`${rolesPath}/list`)
            .then(res => {
                if (res.data.status === 'SUCCESS') {
                    setFilterRoles(res.data.data);
                }
            })
            .catch(() => showErrorToast("Failed to fetch roles"));
    }, []);

    useEffect(() => {
        const handleClickOutside = e => {
            if (filterRef.current && !filterRef.current.contains(e.target)) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const startIndex = (currentPage - 1) * driversPerPage;

    const filteredDrivers = useMemo(() => {
        if (!searchTerm) return drivers;
        const lowerSearch = searchTerm.toLowerCase();
        return drivers.filter(d =>
            d.first_name.toLowerCase().includes(lowerSearch) ||
            d.last_name.toLowerCase().includes(lowerSearch) ||
            (d.driverDetails?.license_no?.toLowerCase().includes(lowerSearch))
        );
    }, [drivers, searchTerm]);

    const currentDrivers = useMemo(() => {
        return filteredDrivers.slice(startIndex, startIndex + driversPerPage);
    }, [filteredDrivers, currentPage, driversPerPage]);

    const totalDrivers = filteredDrivers.length;
    const totalPages = Math.ceil(totalDrivers / driversPerPage);


    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) setCurrentPage(page);
    };


    const handleDeleteDrivers = async () => {
        try {
            const response = await apiClient.put(`${driversPath}/delete`, {
                driver_ids: selectedDriverIds,
            });
            if (response.data.status === 'SUCCESS') {
                showSuccessToast(response.data.message || 'Drivers deleted successfully.');
                setSelectedDriverIds([]);
                fetchDrivers();
            } else {
                showErrorToast(response.data.message || 'Failed to delete drivers.');
            }
        } catch {
            showErrorToast('Error deleting drivers');
        } finally {
            setShowDeleteConfirm(false);
        }
    };

    let userRole = localStorage.getItem("userRole");
    try {
        userRole = JSON.parse(userRole);
    } catch {

    }
    const isUser = userRole?.trim().toLowerCase() === "truck-user";


    return (
        <main className="full-wraper">
            <div className="main-wraper">
                <div className="container-fluid">
                    <div className="main-inner">
                        <div className="common-top">
                            <div className="search-bar" style={{ margin: '0 auto', float: 'none' }}>
                                <form onSubmit={(e) => e.preventDefault()}>
                                    <input className="form-control me-2" type="search" placeholder="Search Name/Lic.No" value={searchTerm} onChange={(e) => onSearchChange(e.target.value)} />
                                    <button className="ser-submit" type="submit">
                                        <img src={searchIcon} alt="search" />
                                    </button>
                                </form>
                            </div>
                            {canEditDriver && !isUser && (
                                <a className="post-button" href="#" data-bs-toggle="modal" data-bs-target="#addDriver">
                                    {/* <img src={plusIcon} alt='Add' /> */}
                                    Add Driver
                                </a>
                            )}
                        </div>

                        <div className="table-cvr">
                            <div className="table-inner">
                                <table className="table" id="driversTable">
                                    <thead>
                                        <tr>
                                            <th className='col-hashindex'>#</th>
                                            <th className='col-primary tcd-primary'>First Name</th>
                                            <th className='col-primary tcd-primary'>Last Name</th>
                                            <th className='col-primary'>Email</th>
                                            <th className='col-secondary'>Contact No.</th>
                                            <th className='col-tertiary'>License No.</th>
                                            <th className='col-tertiary'>Created</th>
                                            <th className='col-linkicon'></th>
                                            <th className='col-linkicon'></th>
                                            <th className='col-spacer'></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr><td colSpan="10" className="text-center py-4"><ClipLoader size={30} color="gray" /></td></tr>
                                        ) : currentDrivers.length > 0 ? (
                                            currentDrivers.map((d, i) => (
                                                <tr key={d.id}>
                                                    <td className='col-hashindex'>{startIndex + i + 1}</td>
                                                    <td className='col-primary tcd-primary'>
                                                        <a className="table-link" href="#" onClick={async (e) => {
                                                            e.preventDefault();
                                                            const driverData = await fetchDriverDetails(d.id);
                                                            if (driverData) {
                                                                setEditDriver({ ...driverData, user_role: d.user_role, editMode: isUser ? false : false });
                                                            }
                                                        }}
                                                        >
                                                            {d.first_name}</a></td>
                                                    <td className='col-primary tcd-primary'>{d.last_name}</td>
                                                    <td className='col-primary'>{d.email_id}</td>
                                                    <td className='col-secondary'>{d.contact_no}</td>
                                                    <td className='col-tertiary'>{d.driverDetails?.license_no}</td>
                                                    <td className='col-tertiary'>{moment(d.created_date_time).local().format(DATE_FORMAT)}</td>
                                                    <td className='col-linkicon text-center'>
                                                        {!isUser && canEditDriver && (
                                                            <a href="#" className='dr-edt' onClick={async (e) => {
                                                                e.preventDefault();
                                                                const driverData = await fetchDriverDetails(d.id);
                                                                if (driverData) {
                                                                    setEditDriver({ ...driverData, user_role: d.user_role, editMode: true });
                                                                }
                                                            }}
                                                            >
                                                                <img className="tb-icon" src={editIcon} alt="Edit" />  </a>)}</td>
                                                    <td className='col-linkicon text-center'>
                                                        {!isUser && (
                                                            <a
                                                                href="#"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    if (d.can_delete) {
                                                                        setSelectedDriverIds([d.id]);
                                                                        setShowDeleteConfirm(true);
                                                                    }
                                                                }}
                                                                style={{ pointerEvents: d.can_delete ? 'auto' : 'none', opacity: d.can_delete ? 1 : 0.5 }}
                                                                title={d.can_delete ? "Delete Driver" : "Cannot delete (Truck-Admin / Active Job)"}
                                                            >
                                                                <img src={deleteIcon} alt="delete" className='tb-icon' />
                                                            </a>
                                                        )}
                                                    </td>

                                                    <td className='col-spacer'></td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr><td colSpan="10" className="text-center py-4 text-muted">No Drivers found.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className="dt-layout-row d-flex align-items-center justify-content-between mt-3">
                                <div className="dt-length">
                                    Showing{' '}
                                    <select className="dt-input" value={driversPerPage} onChange={e => { setCurrentPage(1); setDriversPerPage(Number(e.target.value)); }}>
                                        {[5, 10, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>{' '}
                                    of {totalDrivers} entries
                                </div>

                                <div className="dt-paging d-flex align-items-center" style={{ gap: '5px' }}>
                                    <button className="dt-paging-button previous" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)}>Previous</button>
                                    {Array.from({ length: totalPages }).map((_, i) => (
                                        <button key={i + 1} className={`dt-paging-button ${currentPage === i + 1 ? "current" : ""}`} onClick={() => handlePageChange(i + 1)}>{i + 1}</button>
                                    ))}
                                    <button className="dt-paging-button next" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)}>Next</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Modals */}
                <AddDriverModal refreshDriverList={() => fetchDrivers()} />
                {editDriver && (
                    <EditDriverModal
                        show={!!editDriver}
                        onClose={() => setEditDriver(null)}
                        driverdata={editDriver}
                        mode={editDriver.editMode ? "edit" : "view"}
                        defaultTab="info"
                        refreshDriverList={() => fetchDrivers()}
                        roles={filterRoles}
                    />
                )}

                {/* Delete Confirmation */}
                <ConfirmationModal
                    id="deleteConfirmModal"
                    show={showDeleteConfirm}
                    title="Delete Drivers"
                    message={`Are you sure you want to delete ${selectedDriverIds.length} Driver(s)?`}
                    confirmText="Yes"
                    cancelText="Cancel"
                    onConfirm={handleDeleteDrivers}
                    onCancel={() => setShowDeleteConfirm(false)}
                />
            </div>
        </main>
    );
};

export default DriversList;
